import * as RadixAccordion from '@radix-ui/react-accordion';
import { ComponentProps, ReactNode } from 'react';
import styled from 'styled-components';

import { IconChevronDown } from '../../../common/icons/cdl/ChevronDown';
import { IconX } from '../../../common/icons/cdl/X';
import { MiddleDotSeparator } from '../../../common/Separators/MiddleDotSeparator';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { IconButton } from '../../IconButton/IconButton';
import { Text } from '../../Text/Text';

interface StyledAccordionTriggerProps extends ComponentProps<typeof RadixAccordion.Trigger> {
    disabled?: boolean;
}

const StyledAccordionTrigger = styled(RadixAccordion.Trigger)<StyledAccordionTriggerProps>`
    padding: ${theme.space[2]} ${theme.space[8]};
    display: flex;
    gap: ${theme.space[16]};
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border-radius: ${theme.radii[4]};
    border: none;
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    user-select: none;

    &:hover,
    &:focus {
        background-color: ${(props) => (props.disabled ? 'transparent' : theme.colors.background.subtle)};
    }

    &[data-state='open'] {
        background-color: ${theme.colors.background.subtle};

        svg {
            transform: rotate(-180deg);
        }
    }
`;

const StyledAccordionHeader = styled(RadixAccordion.Header)`
    padding: ${theme.space[8]} 0;
    margin: 0;
    position: relative;
`;

const StyledAccordionItem = styled(RadixAccordion.Item)`
    border-bottom: 1px solid ${theme.colors.border.muted};
    min-width: min-content;

    &:last-of-type {
        border: none;
    }
`;

const StyledAccordionContent = styled(RadixAccordion.Content)`
    overflow: hidden;

    &[data-state='open'] {
        animation: slideDown 100ms ease-in-out;
    }

    &[data-state='closed'] {
        animation: slideUp 100ms ease-in-out;
    }

    @keyframes slideDown {
        from {
            opacity: 0;
            height: 0;
        }
        to {
            opacity: 100%;
            height: var(--radix-accordion-content-height);
        }
    }

    @keyframes slideUp {
        from {
            opacity: 100%;
            height: var(--radix-accordion-content-height);
        }
        to {
            opacity: 0;
            height: 0;
        }
    }
`;

export interface AccordionItemProps {
    id: string;
    title: string;
    summary?: ReactNode;
    disabled?: boolean;
    onRemove?: () => void;
    children: ReactNode;
}

export const AccordionItem = (props: AccordionItemProps) => {
    return (
        <StyledAccordionItem value={props.id} disabled={props.disabled}>
            <StyledAccordionHeader>
                <StyledAccordionTrigger disabled={props.disabled}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={4}
                        width="100%"
                        color={props.disabled ? 'foreground.disabled' : 'foreground.muted'}
                    >
                        <Text variant="body" fontWeight={600} style={{ whiteSpace: 'nowrap' }}>
                            {props.title}
                        </Text>
                        {props.summary ? (
                            <>
                                {typeof props.summary === 'string' ? (
                                    <>
                                        <MiddleDotSeparator />
                                        <Text variant="body" style={{ whiteSpace: 'nowrap' }}>
                                            {props.summary}
                                        </Text>
                                    </>
                                ) : (
                                    props.summary
                                )}
                            </>
                        ) : null}
                    </Box>

                    <Box
                        display="flex"
                        alignItems="center"
                        color={props.disabled ? 'foreground.disabled' : 'foreground.subtle'}
                    >
                        <Box padding={8}>
                            <IconChevronDown height={16} width={16} style={{ transition: 'transform 150ms' }} />
                        </Box>
                    </Box>
                </StyledAccordionTrigger>

                {props.onRemove ? (
                    <Box position="absolute" right="48px" top="10px">
                        <IconButton
                            type="button"
                            Icon={IconX}
                            onClick={(e) => {
                                e.stopPropagation();
                                props.onRemove?.();
                            }}
                        />
                    </Box>
                ) : null}
            </StyledAccordionHeader>

            <StyledAccordionContent>
                <Box padding={8} paddingTop={0}>
                    {props.children}
                </Box>
            </StyledAccordionContent>
        </StyledAccordionItem>
    );
};
